import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Switch from "@mui/material/Switch";
import DeleteIcon from "@mui/icons-material/Delete";
import ReplayIcon from "@mui/icons-material/Replay";
import { visuallyHidden } from "@mui/utils";
import PrintIcon from "@mui/icons-material/Print";
import Skeleton from "@mui/material/Skeleton";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../customComponents/styled/styledTable";
import { useReactToPrint } from "react-to-print";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { FilledButton } from "../../customComponents/styled/styledButtons";
import { StyledTextField } from "../../customComponents/styled/styledInputs";
import {
  useLazyGetClassroomStudentsAllQuery,
  useGetClassroomStudentsPaginatedQuery,
} from "../classroom/classApiSlice";
import { Checkbox, LinearProgress, TableCell } from "@mui/material";
import UserAvatar from "../../common/UserAvatar";
import { useDispatch } from "react-redux";
import { apiSlice } from "../../../app/apiSlice";
import EditStudentDialog from "./EditStudentDialog";
import CustomWarningDialog from "../../customComponents/CustomWarningDialog";
import { useDeleteStudentMutation } from "./studentApiSlice";
import {
  useAdminResetPasswordMutation,
  useGetPublicSchoolInfoQuery,
  useToggleUserStatusMutation,
} from "../../../app/authApiSlice";
import StudentIdCard from "./StudentIdCard";
import toast from "../../../helpers/toast";
import PromoteStudentsDialog from "./PromoteStudentsDialog";
import GraduateStudentsDialog from "./GraduateStudentsDialog";
import PrintPreparationModal from "../../../customComponents/PrintPreparationModal";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useGetSessionQuery } from "../settings/session/sessionApiSlice";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
  const stabilizedThis = array?.map((el, index) => [el, index]);
  stabilizedThis?.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis?.map((el) => el[0]);
}

function EnhancedTableHead(props) {
  const headCells = [
    {
      id: "photo",

      label: "Photo",
    },
    {
      id: "reg_number",
      label: "reg number",
    },
    {
      id: "schoola_id",
      label: "Schoola ID",
    },
    {
      id: "name",
      label: "Name",
    },
    {
      id: "status",
      label: "Status",
    },
    {
      id: "id_card",
      label: "id card",
    },
    {
      id: "reset",
      label: "Reset",
    },
    {
      id: "delete",
      label: "Delete",
    },
  ];

  const {
    order,
    orderBy,
    onRequestSort,
    numSelected,
    rowCount,
    onSelectAllClick,
  } = props;

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <StyledTableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  rows: PropTypes.array.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
};

const EnhancedTableToolbar = (props) => {
  const {
    setSearchkey,
    searchKey,
    handlePrint,
    getStudentsLoading,
    numSelected,
    handlePromoteSelected,
    handleGraduateSelected,
  } = props;

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },

        "& .search": {
          margin: "0px 5px",
        },
      }}
    >
      {getStudentsLoading && <LinearProgress />}
      {numSelected > 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            background: "#F5F5F5",
            padding: 1,
          }}
        >
          <Typography
            sx={{ flex: "1 1 100%" }}
            color="inherit"
            variant="subtitle1"
            component="div"
          >
            {numSelected} selected
          </Typography>
          <FilledButton onClick={handlePromoteSelected}>Promote</FilledButton>
          <FilledButton
            color="error"
            onClick={handleGraduateSelected}
            sx={{ ml: 1 }}
          >
            Graduate
          </FilledButton>
        </Box>
      ) : (
        <>
          <FilledButton onClick={handlePrint}>
            Print
            <PrintIcon />
          </FilledButton>
          <StyledTextField
            className="search"
            placeholder="search..."
            value={searchKey}
            onChange={(e) => setSearchkey(e.target.value)}
          />
        </>
      )}
    </Toolbar>
  );
};

function EnhancedTable({ classroom, isOpen, onToggle }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("name");
  const [page, setPage] = React.useState(1);
  const [perPage, setPerPage] = React.useState(10);
  const [focusedStudent, setFocusedStudent] = React.useState(null);
  const [openEdit, setOpenEdit] = React.useState(false);
  const [selected, setSelected] = React.useState([]);
  const [openWarning, setOpenWarning] = React.useState(false);
  const [openResetWarning, setOpenResetWarning] = React.useState(false);
  const [openPromoteStudents, setOpenPromoteStudents] = React.useState(false);
  const [openGraduateStudents, setOpenGraduateStudents] = React.useState(false);
  const [openIdCard, setOpenIdCard] = React.useState(false);
  const [searchInput, setSearchInput] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [openPrintPrep, setOpenPrintPrep] = React.useState(false);
  const [studentsToPrint, setStudentsToPrint] = React.useState([]);
  const [printDataReady, setPrintDataReady] = React.useState(false);

  const componentToPrintRef = React.useRef();

  // Debounce search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const queryArgs = {
    classroom_id: classroom?.id,
    page,
    per_page: perPage,
    q: debouncedSearch,
  };

  const { data: studentsData, isFetching: getStudentsLoading } =
    useGetClassroomStudentsPaginatedQuery(queryArgs, {
      skip: !isOpen || !classroom?.id,
    });

  const { data: currentSession } = useGetSessionQuery("current");

  const [triggerGetStudentsAll, { isFetching: getStudentsToPrintLoading }] =
    useLazyGetClassroomStudentsAllQuery();

  const { data: publicSchoolInfo } = useGetPublicSchoolInfoQuery();

  const initialRows = React.useMemo(
    () => studentsData?.students || [],
    [studentsData?.students],
  );
  const pagination = studentsData?.pagination;

  const [deleteStudent] = useDeleteStudentMutation();
  const [adminResetPassword] = useAdminResetPasswordMutation();
  const [toggleUserStatus] = useToggleUserStatusMutation();
  const dispatch = useDispatch();

  // Reset selection and page when closing
  React.useEffect(() => {
    if (!isOpen) {
      setSelected([]);
      setPage(1);
      setSearchInput("");
      setDebouncedSearch("");
    }
  }, [isOpen]);

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelected(initialRows.map((item) => item));
      return;
    }
    setSelected([]);
  };

  const handleClickSelect = (event, item) => {
    let newSelected = [...selected];
    if (event.target.checked) {
      newSelected.push(item);
    } else {
      newSelected = newSelected.filter((i) => i !== item);
    }
    setSelected(newSelected);
  };

  const isSelected = (item) => selected.indexOf(item) !== -1;

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handlePrint = useReactToPrint({
    content: () => componentToPrintRef.current,
  });

  // Helper to chunk students into pages (first page has logo header, fits fewer students)
  const chunkStudents = React.useCallback((students) => {
    const pages = [];
    const firstPageSize = 25;
    const otherPageSize = 30;

    if (!students || students.length === 0) return [[]];

    if (students.length <= firstPageSize) {
      pages.push(students);
    } else {
      pages.push(students.slice(0, firstPageSize));
      let index = firstPageSize;
      while (index < students.length) {
        pages.push(students.slice(index, index + otherPageSize));
        index += otherPageSize;
      }
    }
    return pages;
  }, []);

  const handlePrintNow = async () => {
    if (isMobile) {
      // Mobile: generate PDF with html2canvas + jspdf page-by-page
      try {
        const el = componentToPrintRef.current;
        if (!el) return;

        // Temporarily show the element so html2canvas can capture it
        const wrapper = el.parentElement;
        wrapper.style.display = "block";
        wrapper.style.position = "absolute";
        wrapper.style.left = "-9999px";
        wrapper.style.width = "794px"; // Standard A4 width in pixels at 96 DPI to prevent table shrinking/wrapping on mobile

        const pageContainers = el.querySelectorAll(".print-page-container");
        const pdf = new jsPDF("p", "mm", "a4");
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        for (let i = 0; i < pageContainers.length; i++) {
          const pageEl = pageContainers[i];
          const canvas = await html2canvas(pageEl, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: "#ffffff",
          });

          const imgData = canvas.toDataURL("image/png");

          // Use full width of PDF since the HTML page container itself has padding (16px)
          let imgWidth = pdfWidth;
          let imgHeight = (canvas.height * imgWidth) / canvas.width;

          // Safeguard: scale down if height exceeds the PDF page height
          if (imgHeight > pdfHeight) {
            imgHeight = pdfHeight;
            imgWidth = (canvas.width * imgHeight) / canvas.height;
          }

          // Center horizontally if scaled down
          const xOffset = (pdfWidth - imgWidth) / 2;

          if (i > 0) pdf.addPage();
          pdf.addImage(imgData, "PNG", xOffset, 0, imgWidth, imgHeight);
        }

        // Restore hidden state
        wrapper.style.display = "none";
        wrapper.style.position = "";
        wrapper.style.left = "";
        wrapper.style.width = "";

        const filename = `${classroom?.name || "students"}_list.pdf`;
        pdf.save(filename);
      } catch (error) {
        toast.error("Failed to generate PDF, please try again");
      }
    } else {
      // Desktop: use react-to-print
      setTimeout(() => handlePrint(), 100);
    }
    setOpenPrintPrep(false);
    setPrintDataReady(false);
  };

  const handlePrintClick = async () => {
    setPrintDataReady(false);
    setOpenPrintPrep(true);
    try {
      const response = await triggerGetStudentsAll({
        classroom_id: classroom?.id,
      }).unwrap();

      // Sort alphabetically by first name, then last name
      const sortedResponse = [...(response || [])].sort((a, b) => {
        const firstA = (a.first_name || "").trim().toLowerCase();
        const firstB = (b.first_name || "").trim().toLowerCase();
        if (firstA !== firstB) return firstA.localeCompare(firstB);

        const lastA = (a.last_name || "").trim().toLowerCase();
        const lastB = (b.last_name || "").trim().toLowerCase();
        return lastA.localeCompare(lastB);
      });

      setStudentsToPrint(sortedResponse);
      setPrintDataReady(true);
    } catch (error) {
      setOpenPrintPrep(false);
      toast.error("preparing list failed please try again");
    }
  };

  const handleDelete = async () => {
    try {
      const response = await deleteStudent(focusedStudent).unwrap();
      if (response) {
        toast.success("Student Deleted Successfully");
      }
    } catch (error) {
      toast.error("error deleting student");
    }
  };

  const handleResetPassword = async () => {
    try {
      await adminResetPassword(focusedStudent?.user?.id).unwrap();
      toast.success("Student password reset to default successfully");
    } catch (error) {
      toast.error(error?.message || "Failed to reset password");
    }
  };

  const handleToggleStatus = async (row) => {
    const patchResult = dispatch(
      apiSlice.util.updateQueryData(
        "getClassroomStudentsPaginated",
        queryArgs,
        (draft) => {
          const list = draft?.students || draft;
          if (Array.isArray(list)) {
            const student = list.find((s) => s.user?.id === row.user?.id);
            if (student?.user) student.user.is_active = !student.user.is_active;
          }
        },
      ),
    );
    try {
      await toggleUserStatus(row?.user?.id).unwrap();
    } catch (error) {
      patchResult.undo();
      toast.error(error?.message || "Failed to update status");
    }
  };

  const handlePromoteSelected = async () => {
    setOpenPromoteStudents(true);
  };

  const handleGraduateSelected = async () => {
    setOpenGraduateStudents(true);
  };

  const sortedRows = React.useMemo(() => {
    if (!initialRows?.length) return [];
    return stableSort(initialRows, getComparator(order, orderBy));
  }, [initialRows, order, orderBy]);

  // ── Overlay (closed state) ────────────────────────────────────────────────
  if (!isOpen) {
    return (
      <Box sx={{ position: "relative", minHeight: 160, overflow: "hidden" }}>
        {/* Blurred skeleton preview */}
        <Box sx={{ filter: "blur(3px)", pointerEvents: "none", p: 1 }}>
          {[...Array(4)].map((_, i) => (
            <Box key={i} sx={{ display: "flex", gap: 2, mb: 1 }}>
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="rectangular" height={32} sx={{ flex: 1 }} />
              <Skeleton variant="rectangular" width={80} height={32} />
              <Skeleton variant="rectangular" width={60} height={32} />
            </Box>
          ))}
        </Box>

        {/* Centered "Load Students" button overlay */}
        <Paper
          elevation={6}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            px: 3,
            py: 2,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {classroom?.name}
          </Typography>
          <Button
            variant="contained"
            size="medium"
            onClick={() => onToggle(classroom?.id)}
          >
            Load Students
          </Button>
        </Paper>
      </Box>
    );
  }

  // ── Open (loaded) state ───────────────────────────────────────────────────
  return (
    <Box sx={{ width: "100%" }}>
      {/* Modals */}
      <EditStudentDialog
        open={openEdit}
        setOpen={setOpenEdit}
        selectedStudent={focusedStudent}
        currentClassroom={classroom?.id}
      />
      <PromoteStudentsDialog
        open={openPromoteStudents}
        setOpen={setOpenPromoteStudents}
        selectedStudents={selected}
        setSelectedStudents={setSelected}
        currentClassroom={classroom?.id}
      />
      <GraduateStudentsDialog
        open={openGraduateStudents}
        setOpen={setOpenGraduateStudents}
        selectedStudents={selected}
        setSelectedStudents={setSelected}
        currentClassroom={classroom?.id}
      />
      <CustomWarningDialog
        open={openWarning}
        setOpen={setOpenWarning}
        item={focusedStudent}
        action={handleDelete}
        message="Are you sure you want to delete this student?"
      />
      <CustomWarningDialog
        open={openResetWarning}
        setOpen={setOpenResetWarning}
        item={focusedStudent}
        action={handleResetPassword}
        message="Reset this student's password to the default?"
      />
      <StudentIdCard
        open={openIdCard}
        onClose={() => setOpenIdCard(false)}
        student={focusedStudent}
      />
      <PrintPreparationModal
        open={openPrintPrep}
        ready={printDataReady}
        onPrint={handlePrintNow}
        isMobile={isMobile}
      />

      <div style={{ display: "none" }}>
        <div ref={componentToPrintRef} style={{ background: "#ffffff" }}>
          {chunkStudents(studentsToPrint).map(
            (pageStudents, pageIndex, allPages) => (
              <div
                key={pageIndex}
                className="print-page-container"
                style={{
                  pageBreakAfter:
                    pageIndex === allPages.length - 1 ? "auto" : "always",
                  padding: "16px",
                  background: "#ffffff",
                  boxSizing: "border-box",
                }}
              >
                {pageIndex === 0 ? (
                  <Box
                    sx={{
                      textAlign: "center",
                      mb: 2,
                      pb: 2,
                      borderBottom: "2px solid #000",
                    }}
                  >
                    {/* print page header */}
                    {publicSchoolInfo?.logo && (
                      <img
                        src={publicSchoolInfo.logo}
                        alt={publicSchoolInfo.name}
                        style={{
                          width: 72,
                          height: 72,
                          borderRadius: 8,
                          marginBottom: 8,
                          objectFit: "cover",
                        }}
                      />
                    )}
                    {publicSchoolInfo?.name && (
                      <Typography
                        sx={{
                          fontSize: "1.1rem",
                          fontWeight: 700,
                          letterSpacing: "0.02em",
                          color: "#000",
                          lineHeight: 1.3,
                        }}
                      >
                        {publicSchoolInfo.name}
                      </Typography>
                    )}
                    <Typography
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 600,
                        color: "#000",
                        mt: 0.5,
                      }}
                    >
                      {classroom?.name} — Student List
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        fontWeight: 400,
                        color: "#444",
                        mt: 0.25,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                      }}
                    >
                      {currentSession?.name}
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                      pb: 1,
                      borderBottom: "1px solid #000",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        color: "#000",
                      }}
                    >
                      {classroom?.name} — Student List (Continued)
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        fontWeight: 500,
                        color: "#444",
                      }}
                    >
                      Page {pageIndex + 1} of {allPages.length}
                    </Typography>
                  </Box>
                )}

                {/* Force all text black when printing, regardless of dark mode */}
                <style>{`@media print { * { color: #000 !important; -webkit-print-color-adjust: exact; } }`}</style>

                <TableContainer sx={{ p: 0 }}>
                  <Table>
                    <TableHead>
                      <TableRow
                        sx={{
                          borderBottom: "2px solid #000",
                          backgroundColor: "#f5f5f5",
                        }}
                      >
                        <TableCell
                          sx={{
                            color: "#000 !important",
                            fontWeight: 700,
                            fontSize: "0.78rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            padding: "4px 8px",
                          }}
                        >
                          S/N
                        </TableCell>
                        <TableCell
                          sx={{
                            color: "#000 !important",
                            fontWeight: 700,
                            fontSize: "0.78rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            padding: "4px 8px",
                          }}
                        >
                          Photo
                        </TableCell>
                        <TableCell
                          sx={{
                            color: "#000 !important",
                            fontWeight: 700,
                            fontSize: "0.78rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            padding: "4px 8px",
                          }}
                        >
                          Name
                        </TableCell>
                        <TableCell
                          sx={{
                            color: "#000 !important",
                            fontWeight: 700,
                            fontSize: "0.78rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            padding: "4px 8px",
                          }}
                        >
                          Reg Number
                        </TableCell>
                        <TableCell
                          sx={{
                            color: "#000 !important",
                            fontWeight: 700,
                            fontSize: "0.78rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            padding: "4px 8px",
                          }}
                        >
                          Schoola ID
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pageStudents.map((row, i) => {
                        const globalIndex =
                          pageIndex === 0 ? i : 25 + (pageIndex - 1) * 30 + i;
                        return (
                          <TableRow key={row.id || i}>
                            <TableCell
                              sx={{
                                color: "#000 !important",
                                fontSize: "0.8rem",
                                padding: "4px 8px",
                              }}
                            >
                              {globalIndex + 1}
                            </TableCell>
                            <TableCell
                              component="th"
                              scope="row"
                              sx={{ padding: "4px 8px" }}
                            >
                              <UserAvatar
                                src={row.photo}
                                name={`${row.first_name || ""} ${row.last_name || ""}`.trim()}
                                sx={{
                                  width: 24,
                                  height: 24,
                                  fontSize: "0.6rem",
                                }}
                              />
                            </TableCell>
                            <TableCell
                              sx={{
                                color: "#000 !important",
                                fontSize: "0.8rem",
                                fontWeight: 500,
                                padding: "4px 8px",
                              }}
                            >
                              {(row.first_name || "").replace(/\b\w/g, (c) =>
                                c.toUpperCase(),
                              )}{" "}
                              {(row.last_name || "").replace(/\b\w/g, (c) =>
                                c.toUpperCase(),
                              )}
                            </TableCell>
                            <TableCell
                              sx={{
                                color: "#000 !important",
                                fontSize: "0.8rem",
                                padding: "4px 8px",
                              }}
                            >
                              {row.reg_number}
                            </TableCell>
                            <TableCell
                              sx={{
                                color: "#000 !important",
                                fontSize: "0.8rem",
                                padding: "4px 8px",
                              }}
                            >
                              {row.user?.schoola_id || "—"}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            ),
          )}
        </div>
      </div>

      <EnhancedTableToolbar
        setSearchkey={setSearchInput}
        searchKey={searchInput}
        handlePrint={handlePrintClick}
        handlePromoteSelected={handlePromoteSelected}
        handleGraduateSelected={handleGraduateSelected}
        getStudentsLoading={getStudentsLoading}
        numSelected={selected?.length}
      />

      {getStudentsLoading && <LinearProgress />}
      {isMobile ? (
        <Box sx={{ p: 1.5 }}>
          {sortedRows?.map((row) => {
            const isItemSelected = isSelected(row);
            return (
              <Box
                key={row.id}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 1.5,
                  mb: 1.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Checkbox
                    size="small"
                    checked={isItemSelected}
                    onChange={(event) => handleClickSelect(event, row)}
                  />
                  <UserAvatar
                    src={row.photo}
                    name={`${row.first_name || ""} ${row.last_name || ""}`.trim()}
                    sx={{ width: 36, height: 36, fontSize: "0.9rem" }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        textTransform: "capitalize",
                        color: "primary.main",
                        fontWeight: 600,
                      }}
                      onClick={() => {
                        setFocusedStudent(row);
                        setOpenEdit(true);
                      }}
                    >
                      {row.first_name} {row.last_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Reg: {row.reg_number || "—"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Schoola ID: {row.user?.schoola_id || "—"}
                    </Typography>
                  </Box>
                  <Switch
                    size="small"
                    checked={row.user?.is_active ?? true}
                    onChange={() => handleToggleStatus(row)}
                  />
                </Box>

                <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
                  <FilledButton
                    onClick={() => {
                      setFocusedStudent(row);
                      setOpenIdCard(true);
                    }}
                  >
                    <PrintIcon />
                  </FilledButton>
                  <FilledButton
                    onClick={() => {
                      setFocusedStudent(row);
                      setOpenResetWarning(true);
                    }}
                  >
                    <ReplayIcon />
                  </FilledButton>
                  <FilledButton
                    color="error"
                    onClick={() => {
                      setFocusedStudent(row);
                      setOpenWarning(true);
                    }}
                  >
                    <DeleteIcon />
                  </FilledButton>
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size="small"
          >
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              numSelected={selected?.length}
              onSelectAllClick={handleSelectAllClick}
              rowCount={initialRows?.length}
            />
            <TableBody>
              {sortedRows?.map((row) => {
                const isItemSelected = isSelected(row);
                return (
                  <StyledTableRow hover tabIndex={-1} key={row.id}>
                    <TableCell
                      padding="checkbox"
                      onClick={(event) => handleClickSelect(event, row)}
                    >
                      <Checkbox color="primary" checked={isItemSelected} />
                    </TableCell>
                    <StyledTableCell
                      component="th"
                      scope="row"
                      sx={{ paddingLeft: "10px" }}
                    >
                      <UserAvatar
                        src={row.photo}
                        name={`${row.first_name || ""} ${row.last_name || ""}`.trim()}
                        sx={{ width: 24, height: 24, fontSize: "0.6rem" }}
                      />
                    </StyledTableCell>
                    <StyledTableCell>{row.reg_number}</StyledTableCell>
                    <StyledTableCell>{row.user?.schoola_id}</StyledTableCell>
                    <StyledTableCell>
                      <Box
                        className="tableCellLink"
                        onClick={() => {
                          setFocusedStudent(row);
                          setOpenEdit(true);
                        }}
                      >
                        {row.first_name} {row.last_name}
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Switch
                        size="small"
                        checked={row.user?.is_active ?? true}
                        onChange={() => handleToggleStatus(row)}
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      <PrintIcon
                        className="tableCellLink"
                        onClick={() => {
                          setFocusedStudent(row);
                          setOpenIdCard(true);
                        }}
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      <ReplayIcon
                        className="tableCellLink"
                        sx={{ color: "primary.safeOnLight" }}
                        onClick={() => {
                          setFocusedStudent(row);
                          setOpenResetWarning(true);
                        }}
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      <DeleteIcon
                        className="tableCellLink"
                        onClick={() => {
                          setFocusedStudent(row);
                          setOpenWarning(true);
                        }}
                      />
                    </StyledTableCell>
                  </StyledTableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={pagination?.total_items ?? 0}
        rowsPerPage={perPage}
        page={page - 1}
        onPageChange={(_, newPage) => {
          setPage(newPage + 1);
          setSelected([]);
        }}
        onRowsPerPageChange={(e) => {
          setPerPage(parseInt(e.target.value, 10));
          setPage(1);
          setSelected([]);
        }}
      />
    </Box>
  );
}

export default EnhancedTable;
